import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
  type ReactNode,
} from 'react'
import {
  CheckCircleIcon,
  XCircleIcon,
  InformationCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import clsx from 'clsx'

// ── Types ─────────────────────────────────────────────────────────────────────

export type ToastType = 'success' | 'error' | 'info'

interface ToastItem {
  id:      string
  message: string
  type:    ToastType
}

interface ToastContextValue {
  toast:   (message: string, type?: ToastType) => void
  toasts:  ToastItem[]
  dismiss: (id: string) => void
}

// ── Context ───────────────────────────────────────────────────────────────────

const ToastContext = createContext<ToastContextValue | null>(null)

// ── Provider ──────────────────────────────────────────────────────────────────
//
// Mount in App.tsx above AppShell so all pages can use useToast().

export function ToastProvider({ children }: { children: ReactNode }): JSX.Element {
  const [toasts, setToasts] = useState<ToastItem[]>([])

  /**
   * The auto-dismiss timers, held so they can be cancelled.
   *
   * Each toast used to schedule `setTimeout(() => dismiss(id), 4000)` and forget it. Nothing owned the
   * handle, so nothing could cancel it: four seconds after ANY toast, a setState fired whether or not
   * the provider was still mounted. In the browser that is a leak — navigate away and the timer still
   * holds the closure — and React 18 warns about the update.
   *
   * In the test suite it was worse than a warning. jsdom is torn down when a file finishes, so a timer
   * outliving its file threw `ReferenceError: window is not defined` from inside React's scheduler.
   * Vitest reports that as an unhandled error and FAILS THE RUN — with every test passing. That is
   * exactly what it looked like: `124 passed (124)`, `1437 passed (1437)`, `Errors 1 error`, exit 1.
   *
   * Whether the timer outlived its file depended on machine load, so it was intermittent, unattributable
   * (the error is reported against whichever file was running, not the one that raised the toast), and
   * green on every re-run — which taught everyone to re-run instead of read. Reproduced 3 times in 4
   * with `turbo build test --force`, which loads the machine enough to widen the window.
   */
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>())

  const dismiss = useCallback((id: string) => {
    const timer = timers.current.get(id)
    if (timer !== undefined) {
      // Dismissed by hand before its time — drop the pending timer rather than leaving it to fire
      // into an empty list later.
      clearTimeout(timer)
      timers.current.delete(id)
    }
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  const toast = useCallback(
    (message: string, type: ToastType = 'info') => {
      const id = `${Date.now()}-${Math.random()}`
      setToasts((prev) => [...prev, { id, message, type }])
      timers.current.set(id, setTimeout(() => dismiss(id), 4000))
    },
    [dismiss],
  )

  // Unmount cancels every pending auto-dismiss. Without this the provider can be gone and its timers
  // still queued — which is the whole bug above.
  useEffect(() => {
    const pending = timers.current
    return () => {
      for (const timer of pending.values()) clearTimeout(timer)
      pending.clear()
    }
  }, [])

  return (
    <ToastContext.Provider value={{ toast, toasts, dismiss }}>
      {children}
    </ToastContext.Provider>
  )
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useToast(): Pick<ToastContextValue, 'toast'> {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast must be used within a ToastProvider')
  return { toast: ctx.toast }
}

// ── Optional hook ─────────────────────────────────────────────────────────────
//
// Like useToast(), but returns a no-op when rendered outside a ToastProvider
// instead of throwing.  Used by components that surface a toast as a nice-to-have
// (e.g. the Compare footer's PR link) but must also render in test harnesses /
// contexts that don't mount the provider.

export function useToastOptional(): Pick<ToastContextValue, 'toast'> {
  const ctx = useContext(ToastContext)
  return { toast: ctx?.toast ?? (() => {}) }
}

// ── ToastContainer ────────────────────────────────────────────────────────────
//
// Mount inside AppShell.tsx.
// Returns null gracefully if rendered outside a ToastProvider (e.g. in tests
// that don't need toasts).

export function ToastContainer(): JSX.Element {
  const ctx = useContext(ToastContext)
  if (!ctx || ctx.toasts.length === 0) return <></>

  return (
    <div
      aria-live="polite"
      className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 max-w-sm w-full"
    >
      {ctx.toasts.map((t) => (
        <ToastBanner key={t.id} item={t} onDismiss={ctx.dismiss} />
      ))}
    </div>
  )
}

// ── Internal: individual banner ───────────────────────────────────────────────

const ICON_MAP = {
  success: CheckCircleIcon,
  error:   XCircleIcon,
  info:    InformationCircleIcon,
} as const

function ToastBanner({
  item: t,
  onDismiss,
}: {
  item: ToastItem
  onDismiss: (id: string) => void
}): JSX.Element {
  const Icon = ICON_MAP[t.type]

  return (
    <div
      role="alert"
      className={clsx(
        'flex items-start gap-3 bg-surface rounded-card shadow-card-lg px-4 py-3 border-l-4',
        t.type === 'success' && 'border-add',
        t.type === 'error'   && 'border-del',
        t.type === 'info'    && 'border-info',
      )}
    >
      <Icon
        className={clsx(
          'h-5 w-5 shrink-0 mt-0.5',
          t.type === 'success' && 'text-add',
          t.type === 'error'   && 'text-del',
          t.type === 'info'    && 'text-info',
        )}
        aria-hidden="true"
      />
      <p className="text-sm text-fg-muted flex-1">{t.message}</p>
      <button
        onClick={() => onDismiss(t.id)}
        aria-label="Dismiss notification"
        className="text-fg-muted hover:text-fg-muted transition-colors shrink-0"
      >
        <XMarkIcon className="h-4 w-4" />
      </button>
    </div>
  )
}
